import React, { useEffect, useState } from 'react';

import clsx from 'clsx';
import { useForm } from 'react-hook-form';

import { LoadingType, setLoadingState } from 'actions';
import { setLoanPortal } from 'actions/dashboardPortal';
import { BankAccount, truvApi } from 'api/TruvApi';
import BlurPopup from 'components/BlurPopup';
import { Button } from 'components/Buttons/Button';
import FormBody from 'components/FormBody';
import { BankAccountContainerType } from 'enums/BankAccountContainerType';
import { BankAccountsStatus } from 'enums/BankAccountsStatus';
import { ButtonType } from 'enums/ButtonType';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import BankAccountContainer from '../BankAccountContainer';
import styles from './BankAccounts.module.scss';
import Section from '../Sections/Section';
import SubSection from '../Sections/SubSection';
import { SubSectionState } from '../Sections/SubSection/SubSection';

export interface BankAccountsProps {
  fundingInformationSectionRolledUp: boolean;
  chosenBankAccount?: BankAccount;
}

const BankAccounts = ({ fundingInformationSectionRolledUp, chosenBankAccount }: BankAccountsProps) => {
  const dispatch = useAppDispatch();
  const { register, handleSubmit, watch, setValue } = useForm({});
  const watcher = watch();
  const [isOpen, setIsOpen] = useState(false);
  const { applicationId } = useAppSelector((state) => state.application);
  const { bankAccounts, bankAccountsStatus } = useAppSelector((state) => state.dashboardPortal);

  const getInfoHeaderValue = () => {
    switch (bankAccountsStatus) {
      case BankAccountsStatus.PendingPayrollProvider:
      case BankAccountsStatus.PendingFinancialAccounts:
        return 'We are attempting to validate your bank account information for funding. Please complete any remaining sections while you wait. This usually takes no more than 5 minutes.';
      case BankAccountsStatus.Done:
        return 'This account will be used to fund your home improvement loan and for your monthly autopay, if you elected for autopay.';
    }
  };

  useEffect(() => {
    register('selected', {});
  }, []);

  const onSubmit = handleSubmit(() => {
    setIsOpen(true);
  });

  const handleNoneOfTheseAccounts = async () => {
    dispatch(
      setLoadingState({
        type: LoadingType.GetBankAccounts,
        loading: true,
      }),
    );

    await truvApi.setBankAccountStatus({
      applicationId: applicationId!,
      status: BankAccountsStatus.FirstAttemptManual,
    });

    dispatch(
      setLoadingState({
        type: LoadingType.GetBankAccounts,
        loading: false,
      }),
    );

    dispatch(
      setLoanPortal({
        bankAccountsStatus: BankAccountsStatus.FirstAttemptManual,
      }),
    );
  };

  const handleClickClose = async () => {
    setIsOpen(false);
  };

  const handleNextButton = async () => {
    setIsOpen(false);

    dispatch(
      setLoadingState({
        type: LoadingType.GetBankAccounts,
        loading: true,
      }),
    );

    await truvApi.saveBankAccountInfo({
      applicationId: applicationId!,
      bankId: watcher.selected,
    });

    dispatch(
      setLoadingState({
        type: LoadingType.GetBankAccounts,
        loading: false,
      }),
    );

    dispatch(
      setLoanPortal({
        bankAccountsStatus: BankAccountsStatus.Done,
        fundingInformationComplete: true,
      }),
    );
  };

  const fundingSubSectionState: SubSectionState = {
    isExist: true,
    isCompleted: fundingInformationSectionRolledUp,
  };

  return (
    <>
      <Section title={'Funding Information'} subSectionStates={[fundingSubSectionState]}>
        <SubSection state={fundingSubSectionState}>
          <div className={styles.mainHeader}>
            {bankAccountsStatus === BankAccountsStatus.ReceivedPayrollProvider ||
            bankAccountsStatus === BankAccountsStatus.ReceivedFinancialAccounts ? (
              <div className={styles.newLine}>
                <p>
                  Through verifying your income, HFS was able to authenticate bank account(s) you have on direct
                  deposit.
                </p>
                <p>
                  Select one (1) of the following bank accounts, which will be used to fund your home improvement loan.
                </p>
                <p>
                  If you selected autopay for this loan, the selected bank account will also be used for your monthly
                  loan payment.
                </p>
              </div>
            ) : (
              getInfoHeaderValue()
            )}
          </div>
          {bankAccountsStatus === BankAccountsStatus.PendingPayrollProvider ||
          bankAccountsStatus === BankAccountsStatus.PendingFinancialAccounts ? (
            <>
              <BankAccountContainer containerType={BankAccountContainerType.Loading} />
            </>
          ) : (
            <></>
          )}
          {bankAccountsStatus === BankAccountsStatus.ReceivedPayrollProvider ||
          bankAccountsStatus === BankAccountsStatus.ReceivedFinancialAccounts ? (
            <>
              <FormBody onSubmit={onSubmit} formBodyClassName={styles.accountsContainer}>
                {bankAccounts.map((account: BankAccount) => {
                  return (
                    <BankAccountContainer
                      key={account.bankId}
                      containerType={BankAccountContainerType.Select}
                      className={clsx(watcher?.selected === account.bankId && styles.active)}
                      bankAccount={{
                        bankName: account.bankName,
                        routingNumber: account.routingNumber,
                        accountType: account.accountType,
                        accountNumber: account.accountNumber,
                      }}
                      onClick={() => {
                        setValue('selected', account.bankId);
                      }}
                    />
                  );
                })}
                <div className={styles.buttonsContainer}>
                  <Button className={styles.submitButton} disabled={!watcher.selected} onClick={onSubmit}>
                    Save
                  </Button>
                  {bankAccountsStatus === BankAccountsStatus.ReceivedPayrollProvider && (
                    <Button
                      className={styles.submitButton}
                      onClick={handleNoneOfTheseAccounts}
                      buttonType={ButtonType.Secondary}
                    >
                      None of these accounts
                    </Button>
                  )}
                </div>
              </FormBody>
              <BlurPopup id="popup" onClose={handleClickClose} open={isOpen} scroll="body" blurDeep={8}>
                <span className={styles.popupText}>
                  By clicking 'yes' you certify that you are an owner of the bank account selected.
                </span>
                <div className={styles.popupBody}>
                  Once you confirm this selection, the bank account for funding of your HFS home improvement loan cannot
                  be changed.
                </div>
                <div className={styles.dialogButtonsContainer}>
                  <Button buttonType={ButtonType.Primary} onClick={handleNextButton}>
                    Yes
                  </Button>
                  <Button buttonType={ButtonType.Secondary} onClick={handleClickClose}>
                    No
                  </Button>
                </div>
              </BlurPopup>
            </>
          ) : (
            <></>
          )}
          {bankAccountsStatus === BankAccountsStatus.Done ? (
            <>
              <BankAccountContainer
                containerType={BankAccountContainerType.Done}
                className={styles.selectedAccount}
                bankAccount={chosenBankAccount}
              />
            </>
          ) : (
            <></>
          )}
        </SubSection>
      </Section>
    </>
  );
};

export default BankAccounts;
